'use client';

import { useBreakpointValue } from '@chakra-ui/react';
import {
  faEllipsisH,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import Link from 'next/link';
import { useMemo } from 'react';

type Props = {
  url: string;
  totalPage: number;
  currentPage: number;
};

type ItemProps = {
  href?: string;
  active?: boolean;
  noBorder?: boolean;
  children?: React.ReactNode;
};

const Item: React.FC<ItemProps> = ({
  href,
  children,
  active = false,
  noBorder = false,
}) => {
  const tagClass = classnames(
    'flex h-[32px] w-[32px] items-center justify-center rounded',
    {
      'border border-solid border-gray-300': !noBorder,
      'active:bg-gray-100': href,
      'bg-gray-100': active,
    },
  );

  return (
    <>
      {href ? (
        <Link className={tagClass} prefetch={false} href={href}>
          {children}
        </Link>
      ) : (
        <div className={tagClass}>{children}</div>
      )}
    </>
  );
};

const Component: React.FC<Props> = ({ currentPage, totalPage, url }) => {
  const outerWindowSize = 1;
  const windowSize = useBreakpointValue({ base: 1, sm: 2 }) as number;

  const getPageLink = (page: number) => {
    const parser = new URL(url, 'http://localhost/');
    const pathname = parser.pathname.replace(/\/page\/[0-9]+/g, '');
    return page > 1 ? `${pathname}/page/${page}` : pathname;
  };

  const prevPageLink = getPageLink(currentPage - 1);
  const nextPageLink = getPageLink(currentPage + 1);

  const pageItems = useMemo(
    () => new Array(totalPage).fill(null).map((_, i) => i + 1),
    [totalPage],
  );

  // 中央分
  const centerPageItems = useMemo(() => {
    const startNum = Math.max(1, currentPage - windowSize);
    const endNum = Math.min(totalPage, currentPage + windowSize);
    return pageItems.filter(page => startNum <= page && page <= endNum);
  }, [currentPage, windowSize, pageItems]);

  // 先頭
  const firstOuterItems = useMemo(() => {
    return pageItems
      .filter(page => page < (centerPageItems[0] as number))
      .slice(0, outerWindowSize);
  }, [centerPageItems, pageItems, outerWindowSize]);

  // 末尾
  const lastOuterItems = useMemo(() => {
    return pageItems
      .filter(
        page => page > (centerPageItems[centerPageItems.length - 1] as number),
      )
      .slice(-outerWindowSize);
  }, [outerWindowSize, centerPageItems]);

  // 戻るボタン
  const isPrevButtonShown = useMemo(() => currentPage > 1, [currentPage]);

  // 次へボタン
  const isNextButtonShown = useMemo(
    () => currentPage < totalPage,
    [currentPage, totalPage],
  );

  if (totalPage < 2) {
    return <></>;
  }

  return (
    <div className="flex justify-center gap-x-1">
      {isPrevButtonShown && (
        <Item href={prevPageLink}>
          <FontAwesomeIcon icon={faChevronLeft} fixedWidth />
        </Item>
      )}

      {firstOuterItems.length > 0 && (
        <>
          {firstOuterItems.map(page => (
            <Item key={page} href={getPageLink(page)}>
              <div className="text-sm font-bold text-gray-700">{page}</div>
            </Item>
          ))}
          <Item noBorder>
            <FontAwesomeIcon icon={faEllipsisH} fixedWidth />
          </Item>
        </>
      )}

      {centerPageItems.map(page => (
        <Item
          key={page}
          active={page === currentPage}
          href={page !== currentPage ? getPageLink(page) : undefined}
        >
          <div className="text-sm font-bold text-gray-700">{page}</div>
        </Item>
      ))}

      {lastOuterItems.length > 0 && (
        <>
          <Item noBorder>
            <FontAwesomeIcon icon={faEllipsisH} fixedWidth />
          </Item>
          {lastOuterItems.map(page => (
            <Item key={page} href={getPageLink(page)}>
              <div className="text-sm font-bold text-gray-700">{page}</div>
            </Item>
          ))}
        </>
      )}

      {isNextButtonShown && (
        <Item href={nextPageLink}>
          <FontAwesomeIcon icon={faChevronRight} fixedWidth />
        </Item>
      )}
    </div>
  );
};

export default Component;
